import React, { useState, useEffect } from "react";
import {
  Container,
  Grid,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Skeleton,
  Alert,
  Dialog,
} from "@mui/material";
import {
  Download,
  Refresh,
  Visibility,
  People,
  Description,
  Pending,
} from "@mui/icons-material";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import XLSX from "xlsx";
import * as myApi from "../components/Api";
import PaperDetail from "../components/Paper/Detail";

/**
 * Modern Dashboard with Material UI
 * Features:
 * - KPI cards with trends
 * - Interactive pie and bar charts
 * - Recent activity table
 * - Export functionality
 * - Dark mode support
 * - Loading states
 */
export default function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [userStats, setUserStats] = useState({ active: 0, inactive: 0 });
  const [paperStats, setPaperStats] = useState({ pm: 0, pu: 0, total: 0 });
  const [recentPapers, setRecentPapers] = useState([]);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedPaper, setSelectedPaper] = useState(null);

  useEffect(() => {
    const checkDarkMode = () => {
      const htmlElement = document.querySelector("html");
      setIsDarkMode(
        htmlElement.classList.contains("dark") ||
          localStorage.getItem("theme") === "dark"
      );
    };
    checkDarkMode();
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await myApi.get("/admin/dashboard/");
      const data = response.data.data;

      setDashboardData(data);

      // Process user stats
      const active = data.users.active || 0;
      const inactive = data.users.inactive || 0;
      setUserStats({ active, inactive });

      // Process paper stats
      const pmCount = data.papers?.type?.pm?.count || 0;
      const puCount = data.papers?.type?.pu?.count || 0;
      setPaperStats({ pm: pmCount, pu: puCount, total: pmCount + puCount });

      // Process recent papers
      const papers = data.papers?.data || [];
      setRecentPapers(papers.slice(0, 5));
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const userChartData = [
    { name: "Active", value: userStats.active },
    { name: "Inactive", value: userStats.inactive },
  ];

  const paperChartData = [
    { name: "PM", value: paperStats.pm, full: "Paper Mahasiswa" },
    { name: "PU", value: paperStats.pu, full: "Paper Umum" },
  ];

  const COLORS = ["#36d399", "#f87272"];
  const BAR_COLORS = ["#605DFF", "#AD63F6"];

  const handleExportUsers = async () => {
    try {
      const response = await myApi.post("/fetchUserStatus");
      const active = response.data.resultActive || [];
      const inactive = response.data.resultInactive || [];

      const wb = XLSX.utils.book_new();
      const ws1 = XLSX.utils.json_to_sheet(active);
      const ws2 = XLSX.utils.json_to_sheet(inactive);

      XLSX.utils.book_append_sheet(wb, ws1, "Active");
      XLSX.utils.book_append_sheet(wb, ws2, "Inactive");
      XLSX.writeFile(wb, "users-export.xlsx");
    } catch (error) {
      console.error("Error exporting users:", error);
    }
  };

  const handleExportPapers = async () => {
    try {
      const response = await myApi.post("/fetchAllPaperByType");
      const pmPapers = response.data.listPM || [];
      const puPapers = response.data.listPU || [];

      const wb = XLSX.utils.book_new();
      if (pmPapers.length > 0) {
        const ws1 = XLSX.utils.json_to_sheet(pmPapers);
        XLSX.utils.book_append_sheet(wb, ws1, "PM (Paper Mahasiswa)");
      }
      if (puPapers.length > 0) {
        const ws2 = XLSX.utils.json_to_sheet(puPapers);
        XLSX.utils.book_append_sheet(wb, ws2, "PU (Paper Umum)");
      }
      XLSX.writeFile(wb, "papers-export.xlsx");
    } catch (error) {
      console.error("Error exporting papers:", error);
    }
  };

  const getStatusChip = (status, submitStatus) => {
    // If not submitted, show as Draft
    if (submitStatus !== 'submit') {
      return <Chip label="Draft" color="default" size="small" />;
    }
    const statusMap = {
      "-": { label: "Waiting for Approval", color: "warning" },
      "lolos": { label: "Approved", color: "success" },
      "gagal": { label: "Rejected", color: "error" },
    };
    const config = statusMap[status] || { label: status, color: "default" };
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} md={4} key={i}>
              <Skeleton variant="rectangular" height={140} />
            </Grid>
          ))}
          <Grid item xs={12} md={4}>
            <Skeleton variant="rectangular" height={400} />
          </Grid>
          <Grid item xs={12} md={8}>
            <Skeleton variant="rectangular" height={400} />
          </Grid>
        </Grid>
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ mb: { xs: 3, md: 4 }, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
        <Typography variant={{ xs: "h5", sm: "h4" }} fontWeight={600}>
          Dashboard
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={fetchData}
          size="small"
        >
          Refresh
        </Button>
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: { xs: 3, md: 4 } }}>
        {/* Total Users */}
        <Grid item xs={12} sm={6} md={4}>
          <Card
            sx={{
              height: "100%",
              borderRadius: { xs: 2, md: 3 },
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              p: { xs: 0.5, sm: 0 },
            }}
          >
            <CardContent sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: { xs: 1.5, sm: 2 } }}>
                <People sx={{ fontSize: { xs: 32, sm: 40 }, color: "primary.main", mr: { xs: 1.5, sm: 2 } }} />
                <Box>
                  <Typography variant={{ xs: "h5", sm: "h4" }} fontWeight={600}>
                    {userStats.active + userStats.inactive}
                  </Typography>
                  <Typography variant={{ xs: "body2", sm: "body2" }} color="text.secondary">
                    Total Users
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 2 }, flexWrap: "wrap" }}>
                <Chip
                  label={`${userStats.active} Active`}
                  color="success"
                  size="small"
                />
                <Chip
                  label={`${userStats.inactive} Inactive`}
                  color="error"
                  size="small"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Total Papers */}
        <Grid item xs={12} sm={6} md={4}>
          <Card
            sx={{
              height: "100%",
              borderRadius: { xs: 2, md: 3 },
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              p: { xs: 0.5, sm: 0 },
            }}
          >
            <CardContent sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: { xs: 1.5, sm: 2 } }}>
                <Description sx={{ fontSize: { xs: 32, sm: 40 }, color: "secondary.main", mr: { xs: 1.5, sm: 2 } }} />
                <Box>
                  <Typography variant={{ xs: "h5", sm: "h4" }} fontWeight={600}>
                    {paperStats.total}
                  </Typography>
                  <Typography variant={{ xs: "body2", sm: "body2" }} color="text.secondary">
                    Total Papers
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 2 }, flexWrap: "wrap" }}>
                <Chip
                  label={`${paperStats.pm} PM`}
                  sx={{ bgcolor: "primary.light", color: "primary.dark" }}
                  size="small"
                />
                <Chip
                  label={`${paperStats.pu} PU`}
                  sx={{ bgcolor: "secondary.light", color: "secondary.dark" }}
                  size="small"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Pending Papers */}
        <Grid item xs={12} sm={6} md={4}>
          <Card
            sx={{
              height: "100%",
              borderRadius: { xs: 2, md: 3 },
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              p: { xs: 0.5, sm: 0 },
            }}
          >
            <CardContent sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: { xs: 1.5, sm: 2 } }}>
                <Pending sx={{ fontSize: { xs: 32, sm: 40 }, color: "warning.main", mr: { xs: 1.5, sm: 2 } }} />
                <Box>
                  <Typography variant={{ xs: "h5", sm: "h4" }} fontWeight={600}>
                    {recentPapers.filter((p) => p.paper_status === "-").length}
                  </Typography>
                  <Typography variant={{ xs: "body2", sm: "body2" }} color="text.secondary">
                    Waiting for Approval
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 2 }, flexWrap: "wrap" }}>
                <Chip
                  label="Needs Attention"
                  color="warning"
                  size="small"
                  startIcon={<Visibility />}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: { xs: 3, md: 4 } }}>
        {/* User Distribution Pie Chart */}
        <Grid item xs={12} sm={6}>
          <Card sx={{ borderRadius: { xs: 2, md: 3 }, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}>
            <CardContent sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: { xs: 1.5, sm: 2 } }}>
                <Typography variant={{ xs: "h6", sm: "h6" }} fontWeight={600}>
                  User Distribution
                </Typography>
                <Tooltip title="Export Users">
                  <IconButton size="small" onClick={handleExportUsers}>
                    <Download />
                  </IconButton>
                </Tooltip>
              </Box>
              <ResponsiveContainer width="100%" height={{ xs: 250, sm: 300 }}>
                <PieChart>
                  <Pie
                    data={userChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {userChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Paper Types Bar Chart */}
        <Grid item xs={12} sm={6}>
          <Card sx={{ borderRadius: { xs: 2, md: 3 }, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}>
            <CardContent sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: { xs: 1.5, sm: 2 } }}>
                <Typography variant={{ xs: "h6", sm: "h6" }} fontWeight={600}>
                  Paper Types
                </Typography>
                <Tooltip title="Export Papers">
                  <IconButton size="small" onClick={handleExportPapers}>
                    <Download />
                  </IconButton>
                </Tooltip>
              </Box>
              <ResponsiveContainer width="100%" height={{ xs: 250, sm: 300 }}>
                <BarChart data={paperChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <RechartsTooltip />
                  <Legend />
                  <Bar dataKey="value" fill="#605DFF" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recent Papers Table */}
      <Card sx={{ borderRadius: { xs: 2, md: 3 }, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}>
        <CardContent sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: { xs: 2, sm: 3 } }}>
            <Typography variant={{ xs: "h6", sm: "h6" }} fontWeight={600}>
              Recent Papers
            </Typography>
          </Box>
          <TableContainer sx={{ overflowX: "auto", width: "100%" }}>
            <Table sx={{ minWidth: { xs: 400, sm: 600 } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}><strong>Paper Code</strong></TableCell>
                  <TableCell sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}><strong>Title</strong></TableCell>
                  <TableCell sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" }, display: { xs: "none", md: "table-cell" } }}><strong>Author</strong></TableCell>
                  <TableCell sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}><strong>Type</strong></TableCell>
                  <TableCell sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}><strong>Status</strong></TableCell>
                  <TableCell sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" }, display: { xs: "none", sm: "table-cell" } }}><strong>Submission Date</strong></TableCell>
                  <TableCell align="right" sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentPapers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Alert severity="info">No papers submitted yet</Alert>
                    </TableCell>
                  </TableRow>
                ) : (
                  recentPapers.map((paper) => (
                    <TableRow key={paper.paper_code} hover>
                      <TableCell sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>{paper.paper_code}</TableCell>
                      <TableCell sx={{ maxWidth: { xs: 100, sm: 200, md: 300 }, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                        {paper.title}
                      </TableCell>
                      <TableCell sx={{ display: { xs: "none", md: "table-cell" }, fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>{paper.name_1}</TableCell>
                      <TableCell sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                        <Chip
                          label={paper.category === "Mahasiswa" ? "PM" : "PU"}
                          size="small"
                          color={paper.category === "Mahasiswa" ? "primary" : "secondary"}
                        />
                      </TableCell>
                      <TableCell>{getStatusChip(paper.paper_status, paper.submit_status)}</TableCell>
                      <TableCell>
                        {paper.submission_date && paper.submission_date !== "-"
                          ? new Date(paper.submission_date).toLocaleDateString()
                          : "-"}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View Details">
                          <IconButton size="small" color="primary" onClick={() => { setSelectedPaper(paper); setDetailOpen(true); }}>
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <PaperDetail 
          paper={selectedPaper} 
          onClose={() => setDetailOpen(false)}
          onUpdate={(updatedPaper) => {
            setRecentPapers(recentPapers.map(p => p.paper_code === updatedPaper.paper_code ? updatedPaper : p));
          }} 
        />
      </Dialog>
    </Container>
  );
}
